import { Container, Graphics, Sprite, Text, Ticker, type Texture } from 'pixi.js';
import type { SlotTrailState, TrailSquare } from '@goodtime/shared';
import {
  TRAIL_PRIZE_MS,
  TRAIL_ROLL_AFTER_MS,
  TRAIL_SHOW_MS,
  trailBonusOpen,
  trailHint,
  trailMoment,
  trailPrizeText,
  trailSquareLabel,
  trailStepsToBonus,
  type TrailLayout,
} from '../../logic/trail/trail';
import { BoardPanel, woodGradient } from '../board/BoardPanel';
import { BrassButton, paintedPlate } from '../board/BrassButton';
import { animate, COLOURS, displayStyle, ease, uiStyle } from '../board/ui';
import { RoadStrip } from '../road/RoadStrip';

/** The painted pieces the trail is drawn with; any can be missing while
 *  the slot's art loads. */
export interface TrailArt {
  (name: 'bezel' | 'cobbles' | 'path' | 'coin' | 'gem' | 'spin' | 'collector' | 'gift' | 'floor'): Texture | null;
}

/** The trail as plain data, for browser tests (nothing of it is in the DOM). */
export interface TrailPanelSnapshot {
  visible: boolean;
  mode: 'chip' | 'board';
  pinned: boolean;
  level: number;
  position: number;
  meter: number;
  cost: number;
  toBonus: number;
  bonusReady: boolean;
  /** The chip's words ("12/20", "6 to"). */
  chip: string[];
  hint: string;
  /** The prize plate showing after a hop, or ''. */
  prize: string;
}

export type TrailPart = 'chip' | 'board' | 'close' | 'gift';

/** The close button's side. */
const CLOSE_SIZE = 34;

/** A cream × centred in a `size` square, with a dark edge like the captions'. */
function closeCross(size: number): Graphics {
  const arm = size * 0.16;
  const c = size / 2;
  const g = new Graphics();
  const draw = (width: number, color: number, alpha = 1) =>
    g
      .moveTo(c - arm, c - arm)
      .lineTo(c + arm, c + arm)
      .moveTo(c + arm, c - arm)
      .lineTo(c - arm, c + arm)
      .stroke({ width, color, alpha, cap: 'round' });
  draw(6, 0x2a1606, 0.7);
  draw(3, COLOURS.cream);
  return g;
}

const PINNED_KEY = 'goodtime.trail.pinned';

/** Visible, and so is everything it sits in. */
function shownOnStage(node: Container): boolean {
  for (let n: Container | null = node; n; n = n.parent) if (!n.visible) return false;
  return true;
}
const CHIP_H = 40;

function readPinned(): boolean {
  try {
    return window.localStorage.getItem(PINNED_KEY) === '1';
  } catch {
    return false;
  }
}

function writePinned(pinned: boolean): void {
  try {
    if (pinned) window.localStorage.setItem(PINNED_KEY, '1');
    else window.localStorage.removeItem(PINNED_KEY);
  } catch {
    // Storage blocked: the pin lasts for this visit only.
  }
}

/**
 * A slot's Sweet Trail, on the slot's canvas (slots without the road;
 * producer 2026-10-14: everything in the game is drawn in Pixi). Collector
 * symbols fill the meter; a full meter moves the token a square and pays
 * it; the gift at the end opens the pick-a-prize bonus. All state comes
 * from the server.
 *
 * <p>A small chip is always there; the board (the slot's reel floor with a
 * brass edge, and the road rolling towards the token) comes out when the
 * token moves, while the bonus waits, and on a new player's first
 * collection, then tucks away again. A tap on the chip, or on a board that
 * came out by itself, pins the board open, across spins and slots, until
 * its close button is tapped (producer: "if someone opens the trail, then
 * stay open until they close it"). Remembered per device.
 */
export class TrailPanel extends Container {
  private readonly boardLayer = new Container();
  private readonly board = new BoardPanel();
  private readonly strip: RoadStrip;
  private readonly hintText: Text;
  private readonly closeButton: BrassButton;
  private readonly chip = new Container();
  private readonly chipBoard = new BoardPanel();
  private readonly chipBody = new Container();
  private readonly chipPop: Text;
  private readonly prize = new Container();
  private trail: SlotTrailState | null = null;
  private box: TrailLayout | null = null;
  private lastSpinKey = 0;
  private pinned = readPinned();
  private expanded = this.pinned;
  private intro = false;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private prizeTimers: ReturnType<typeof setTimeout>[] = [];
  private breath = 0;
  private breathing = false;
  private boardH = 0;
  private chipW = 0;
  private readonly breathe = (ticker: Ticker): void => {
    this.breath += ticker.deltaMS;
    this.strip.setTokenScale(1 + 0.08 * Math.sin((this.breath / 900) * Math.PI * 2));
  };
  /** A hop's prize has come up on its plate, at `at` (screen pixels), or
   *  null when the panel isn't on screen: the host flies its coins in. */
  onPrize: ((at: { x: number; y: number } | null) => void) | null = null;
  /** A tap on the gift while the bonus waits: the host opens it. */
  onOpenBonus: (() => void) | null = null;
  /** What the slot's collector symbols are called, plural ("gumdrops"). */
  collectorNoun = 'lucky symbols';
  /** A line from the host in place of the hint (the bonus couldn't open),
   *  shown even on a compact board; null for none. */
  private notice: string | null = null;

  constructor(private readonly art: TrailArt) {
    super();
    this.strip = new RoadStrip((name) => (name === 'lamp' ? art('gift') : art(name)), { art: () => art('gift'), caption: 'Bonus' });
    this.hintText = new Text({ text: '', style: { ...uiStyle(13, 0xf0dcb4, '600'), align: 'center', wordWrap: true } });
    this.hintText.anchor.set(0.5, 0);
    // Square, so the plate's round ends meet in a circle; the × is drawn on
    // its centre (a font's × sits off it).
    this.closeButton = new BrassButton({ label: '', kind: 'wood', width: CLOSE_SIZE, height: CLOSE_SIZE, onTap: () => this.close() });
    this.closeButton.addChild(closeCross(CLOSE_SIZE));
    this.closeButton.label = 'trail-close';
    // The board's own tap (pin it) mustn't hear the close button's.
    this.closeButton.on('pointertap', (e) => e.stopPropagation());
    this.chipPop = new Text({ text: '', style: displayStyle(16) });
    this.chipPop.anchor.set(0.5, 1);
    this.chipPop.visible = false;
    this.chip.addChild(this.chipBoard, this.chipBody, this.chipPop);
    this.boardLayer.addChild(this.board, this.strip, this.hintText, this.closeButton);
    this.addChild(this.boardLayer, this.chip, this.prize);

    this.board.eventMode = 'static';
    this.board.cursor = 'pointer';
    this.board.on('pointertap', () => {
      if (!this.pinned) this.pin();
    });
    this.strip.eventMode = 'static';
    this.strip.cursor = 'pointer';
    this.strip.on('pointertap', (e) => {
      e.stopPropagation();
      if (this.trail && trailBonusOpen(this.trail)) this.onOpenBonus?.();
      else if (!this.pinned) this.pin();
    });
    this.chip.eventMode = 'static';
    this.chip.cursor = 'pointer';
    this.chip.on('pointertap', () => this.pin());
    this.prize.eventMode = 'none';
    this.visible = false;
  }

  /** Where the panel goes ({@link trailLayout}). */
  setLayout(box: TrailLayout): void {
    this.box = box;
    this.position.set(Math.round(box.x), Math.round(box.y));
    this.draw();
  }

  /**
   * A new trail state; `spinKey` changes when a spin produced it. A spin's
   * hop rolls the road and brings the board out once the reels' win has
   * shown (`revealDelayMs`); anything else snaps.
   */
  setTrail(trail: SlotTrailState, spinKey: number, revealDelayMs = 0): void {
    const fromSpin = spinKey !== this.lastSpinKey && this.trail !== null;
    this.lastSpinKey = spinKey;
    const wasOpen = this.trail !== null && trailBonusOpen(this.trail);
    this.trail = trail;
    const moment = fromSpin ? trailMoment(trail) : null;
    const moved = trail.steps.length > 0 || trail.collected > 0;
    this.strip.update(
      { squares: trail.squares.map(trailSquareLabel), position: trail.position, meter: trail.meter, cost: trail.stepCost },
      { rolled: fromSpin && moved, collected: trail.collected, hop: trail.steps.length > 0, delayMs: revealDelayMs + TRAIL_ROLL_AFTER_MS },
    );
    const open = trailBonusOpen(trail);
    if (open) this.showFor(null);
    else if (wasOpen && !this.pinned) this.showFor(TRAIL_SHOW_MS);
    if (moment) {
      if (moment.collected > 0) this.popChip(moment.collected);
      if (moment.show) {
        if (moment.show.intro) this.intro = true;
        this.showFor(moment.show.ms, revealDelayMs);
      }
      if (moment.prize) this.showPrize(moment.prize, revealDelayMs);
    }
    this.setBreathing(open);
    this.draw();
  }

  /** Say `text` under the board in place of its hint, or null to stop. */
  setNotice(text: string | null): void {
    if (text === this.notice) return;
    this.notice = text;
    this.draw();
  }

  /** The board is out (pinned or shown by itself). */
  get boardOut(): boolean {
    return this.expanded;
  }

  /** The panel's height as drawn now. */
  get panelHeight(): number {
    return this.expanded ? this.boardH : CHIP_H;
  }

  get snapshot(): TrailPanelSnapshot {
    const t = this.trail;
    return {
      visible: shownOnStage(this),
      mode: this.expanded ? 'board' : 'chip',
      pinned: this.pinned,
      level: t?.level ?? 0,
      position: t?.position ?? 0,
      meter: t?.meter ?? 0,
      cost: t?.stepCost ?? 0,
      toBonus: t ? trailStepsToBonus(t) : 0,
      bonusReady: t ? trailBonusOpen(t) : false,
      chip: this.chipBody.children.filter((c): c is Text => c instanceof Text).map((c) => c.text),
      hint: this.hintText.visible ? this.hintText.text : '',
      prize: this.prize.visible ? ((this.prize.getChildByLabel('prize-text') as Text | null)?.text ?? '') : '',
    };
  }

  /** Tap a part as a player does (browser tests); false if it isn't there. */
  tap(part: TrailPart): boolean {
    const target = this.partTarget(part);
    if (!target) return false;
    target.emit('pointertap', { stopPropagation: () => undefined } as never);
    return true;
  }

  /** Where a part is on the canvas, for a real tap (browser tests). */
  where(part: TrailPart): { x: number; y: number } | null {
    const target = this.partTarget(part);
    if (!target) return null;
    if (part === 'gift') {
      const c = this.strip.tokenCentre;
      return this.strip.toGlobal(c);
    }
    const b = target.getBounds();
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.clearTimers();
    this.setBreathing(false);
    super.destroy(options);
  }

  // ---- showing and hiding -------------------------------------------

  private partTarget(part: TrailPart): Container | null {
    if (!this.visible) return null;
    switch (part) {
      case 'chip':
        return this.expanded ? null : this.chip;
      case 'board':
        return this.expanded ? this.board : null;
      case 'close':
        return this.expanded ? this.closeButton : null;
      case 'gift':
        return this.expanded && this.trail && trailBonusOpen(this.trail) ? this.strip : null;
    }
  }

  private pin(): void {
    this.pinned = true;
    writePinned(true);
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = null;
    this.setExpanded(true);
  }

  private close(): void {
    this.pinned = false;
    writePinned(false);
    if (this.hideTimer) clearTimeout(this.hideTimer);
    if (this.showTimer) clearTimeout(this.showTimer);
    this.hideTimer = this.showTimer = null;
    this.intro = false;
    this.setExpanded(false);
  }

  /** Bring the board out (after `delayMs`) for `ms`, or until further
   *  notice (null). A pinned board never tucks away by itself. */
  private showFor(ms: number | null, delayMs = 0): void {
    if (this.showTimer) clearTimeout(this.showTimer);
    this.showTimer = null;
    const open = (): void => {
      this.setExpanded(true);
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hideTimer =
        ms === null || this.pinned
          ? null
          : setTimeout(() => {
              this.hideTimer = null;
              this.intro = false;
              this.setExpanded(false);
            }, ms);
    };
    if (delayMs > 0) this.showTimer = setTimeout(open, delayMs);
    else open();
  }

  private setExpanded(expanded: boolean): void {
    if (this.destroyed) return;
    const changed = expanded !== this.expanded;
    this.expanded = expanded;
    this.draw();
    if (changed && expanded) {
      // Slides down a little as it fades in.
      void animate(260, (t) => {
        this.boardLayer.alpha = Math.min(1, t * 1.6);
        this.boardLayer.y = -10 * (1 - ease.outBack(t));
        this.boardLayer.scale.set(0.96 + 0.04 * ease.outBack(t));
      });
    }
  }

  private setBreathing(on: boolean): void {
    if (on === this.breathing) return;
    this.breathing = on;
    if (on) Ticker.shared.add(this.breathe);
    else {
      Ticker.shared.remove(this.breathe);
      if (!this.destroyed) this.strip.setTokenScale(1);
    }
  }

  private clearTimers(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    if (this.showTimer) clearTimeout(this.showTimer);
    this.prizeTimers.forEach((t) => clearTimeout(t));
    this.hideTimer = this.showTimer = null;
    this.prizeTimers = [];
  }

  // ---- drawing --------------------------------------------------------

  private sizes(): { tokenSq: number; sq: number; padX: number; padTop: number; padBottom: number } {
    const compact = this.box?.compact ?? true;
    if (this.box?.wide) return { tokenSq: 40, sq: 34, padX: 14, padTop: 8, padBottom: 6 };
    return compact
      ? { tokenSq: 40, sq: 34, padX: 14, padTop: 10, padBottom: 8 }
      : { tokenSq: 50, sq: 42, padX: 18, padTop: 14, padBottom: 10 };
  }

  private draw(): void {
    const box = this.box;
    const trail = this.trail;
    if (!box || !trail) return;
    const { tokenSq, sq, padX, padTop, padBottom } = this.sizes();
    const w = Math.round(box.width);
    const hint = this.notice ?? (this.box?.wide || this.box?.compact ? '' : trailHint(trail, this.intro, this.collectorNoun));
    const stripH = RoadStrip.heightFor(tokenSq, sq, box.compact);
    this.hintText.style.wordWrapWidth = Math.max(80, w - padX * 2);
    this.hintText.text = hint;
    // One line is 22; a notice can run to two.
    const hintH = hint ? 22 + Math.max(0, Math.ceil(this.hintText.height) - 18) : 0;
    this.boardH = padTop + stripH + hintH + padBottom;

    // The board.
    this.board.setFloor(this.art('floor'));
    this.board.resize(w, this.boardH);
    this.board.position.set(0, 0);
    this.strip.layoutStrip(w - padX * 2, tokenSq, sq, box.compact);
    this.strip.position.set(padX, padTop);
    this.hintText.visible = hint !== '';
    this.hintText.position.set(w / 2, padTop + stripH + 2);
    this.closeButton.position.set(w - this.closeButton.buttonWidth + 10, -12);
    this.boardLayer.visible = this.expanded;
    this.boardLayer.pivot.set(w / 2, 0);
    this.boardLayer.x = w / 2;

    // The chip.
    this.chip.visible = !this.expanded;
    this.drawChip(trail, w);
    this.placePrize();
  }

  /** The chip: the collector, the meter on a stretch of road, and how many
   *  squares to the gift. */
  private drawChip(trail: SlotTrailState, width: number): void {
    this.chipBody.removeChildren().forEach((c) => c.destroy({ children: true }));
    let x = 10;
    const mid = CHIP_H / 2;
    const collector = this.art('collector');
    if (collector) {
      const s = new Sprite(collector);
      s.anchor.set(0, 0.5);
      s.width = s.height = 26;
      s.position.set(x, mid);
      this.chipBody.addChild(s);
    }
    x += 30;
    const meterW = 56;
    const cobbles = this.art('path') ?? this.art('cobbles');
    const pct = Math.max(0, Math.min(1, trail.meter / Math.max(1, trail.stepCost)));
    if (cobbles) {
      const dark = new Sprite(cobbles);
      dark.position.set(x, mid - 5);
      dark.width = meterW;
      dark.height = 10;
      dark.tint = 0x5a5048;
      const lit = new Sprite(cobbles);
      lit.position.set(x, mid - 5);
      lit.width = Math.max(1, meterW * pct);
      lit.height = 10;
      this.chipBody.addChild(dark, lit);
    } else {
      const g = new Graphics()
        .roundRect(x, mid - 4, meterW, 8, 4)
        .fill({ color: 0x3a2a14 })
        .roundRect(x, mid - 4, Math.max(2, meterW * pct), 8, 4)
        .fill(woodGradient(0xffe08a, 0xe0a020));
      this.chipBody.addChild(g);
    }
    this.chipPop.position.set(x + meterW / 2, mid - 6);
    x += meterW + 6;
    const count = new Text({ text: `${trail.meter}/${trail.stepCost}`, style: uiStyle(13, COLOURS.gold) });
    count.anchor.set(0, 0.5);
    count.position.set(x, mid);
    x += count.width + 12;
    const toGo = new Text({ text: `${trailStepsToBonus(trail)} to`, style: uiStyle(13, COLOURS.cream) });
    toGo.anchor.set(0, 0.5);
    toGo.position.set(x, mid);
    x += toGo.width + 5;
    const gift = this.art('gift');
    if (gift) {
      const s = new Sprite(gift);
      s.anchor.set(0, 0.5);
      s.width = s.height = 20;
      s.position.set(x, mid);
      this.chipBody.addChild(s);
      x += 20;
    }
    this.chipBody.addChild(count, toGo);
    this.chipW = Math.round(x + 12);
    this.chipBoard.setFloor(this.art('floor'));
    this.chipBoard.resize(this.chipW, CHIP_H);
    this.chip.position.set(Math.round((width - this.chipW) / 2), 0);
    this.chip.hitArea = { contains: (px: number, py: number) => px >= 0 && py >= 0 && px <= this.chipW && py <= CHIP_H };
  }

  private popChip(amount: number): void {
    this.chipPop.text = `+${amount}`;
    this.chipPop.visible = true;
    const y0 = this.chipPop.y;
    void animate(1300, (t) => {
      this.chipPop.alpha = t < 0.15 ? t / 0.15 : t > 0.8 ? 1 - (t - 0.8) / 0.2 : 1;
      this.chipPop.y = y0 - 14 * t;
    }).then(() => {
      if (this.destroyed) return;
      this.chipPop.visible = false;
      this.chipPop.y = y0;
    });
  }

  /** What a hop paid, on a gold plate under the panel for a moment. */
  private showPrize(prize: TrailSquare, delayMs: number): void {
    this.prizeTimers.push(
      setTimeout(() => {
        if (this.destroyed) return;
        this.buildPrize(prize);
        this.prize.visible = true;
        this.placePrize();
        this.onPrize?.(shownOnStage(this) ? this.prize.getGlobalPosition() : null);
        const y0 = this.prize.y;
        void animate(TRAIL_PRIZE_MS, (t) => {
          const k = t < 0.12 ? ease.outBack(t / 0.12) : 1;
          this.prize.scale.set(0.8 + 0.2 * k);
          this.prize.alpha = t < 0.12 ? t / 0.12 : t > 0.85 ? 1 - (t - 0.85) / 0.15 : 1;
          this.prize.y = y0 - (t > 0.85 ? 12 * ((t - 0.85) / 0.15) : 0);
        }).then(() => {
          if (!this.destroyed) this.prize.visible = false;
        });
      }, delayMs),
    );
  }

  private buildPrize(prize: TrailSquare): void {
    this.prize.removeChildren().forEach((c) => c.destroy({ children: true }));
    const text = new Text({ text: trailPrizeText(prize), style: { ...displayStyle(18, 0x3a1a00), stroke: undefined, dropShadow: undefined } });
    text.label = 'prize-text';
    text.anchor.set(0, 0.5);
    const iconTexture = prize.kind === 'COINS' ? this.art('coin') : prize.kind === 'GEMS' ? this.art('gem') : null;
    const iconW = iconTexture ? 24 : 0;
    const h = 36;
    const w = Math.round(text.width + iconW + (iconW ? 6 : 0) + 44);
    const plate = paintedPlate('gold', w, h);
    if (plate) {
      plate.position.set(-w / 2, -h / 2);
      this.prize.addChild(plate);
    } else {
      this.prize.addChild(
        new Graphics()
          .roundRect(-w / 2, -h / 2, w, h, 12)
          .fill(woodGradient(0xffe08a, 0xe0a020))
          .stroke({ color: 0xd9a441, width: 2 }),
      );
    }
    let x = -w / 2 + 22;
    if (iconTexture) {
      const icon = new Sprite(iconTexture);
      icon.anchor.set(0, 0.5);
      icon.width = icon.height = iconW;
      icon.position.set(x, 0);
      this.prize.addChild(icon);
      x += iconW + 6;
    }
    text.position.set(x, 1);
    this.prize.addChild(text);
  }

  private placePrize(): void {
    if (!this.box) return;
    // Riding the panel's bottom edge, so it reaches as little as it can
    // over what's below.
    this.prize.position.set(Math.round(this.box.width / 2), this.panelHeight - 2);
  }
}
